import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Package, Sparkles, ShirtIcon, MapPin, Phone, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Companies = () => {
  const companies = [
    {
      id: 'packaging',
      name: 'Aumkar International',
      tagline: 'Advanced Packaging Manufacturing',
      location: 'Dundigal, Hyderabad, India',
      phone: '+91 90362 40229',
      phone2: '+91 62817 56651',
      icon: Package,
      image: 'https://images.pexels.com/photos/5025503/pexels-photo-5025503.jpeg',
      description: 'Our flagship company specializes in manufacturing high-quality packaging products for various industries. With state-of-the-art facilities and experienced workforce, we deliver excellence in every package.',
      products: [
        'Cardboard Boxes',
        'Custom Packaging Solutions',
        'Wrappers & Wrapping Materials',
        'Bulk Packaging Materials',
        'Corrugated Boxes',
        'Industrial Packaging',
      ],
      features: [
        'Large-scale production capabilities',
        'Custom design services',
        'Quality assurance at every step',
        'Competitive pricing',
        'Timely delivery',
        'Eco-friendly options available',
      ],
    },
    {
      id: 'cleaning',
      name: 'Remas Cleaning Services',
      tagline: 'Professional Cleaning Solutions',
      location: 'Muscat, Oman',
      phone: '+968 9490 0565',
      icon: Sparkles,
      image: 'https://images.pexels.com/photos/4239036/pexels-photo-4239036.jpeg',
      description: 'Remas Cleaning Services provides comprehensive cleaning solutions for residential, commercial, and industrial clients in Oman. Our trained professionals ensure spotless results every time.',
      services: [
        'Residential Cleaning',
        'Commercial Cleaning',
        'Industrial Cleaning',
        'Deep Cleaning & Sanitization',
        'Window Cleaning',
        'Carpet & Upholstery Cleaning',
      ],
      features: [
        'Trained & vetted staff',
        'Eco-friendly cleaning products',
        'Flexible scheduling',
        'Competitive rates',
        'Insured services',
        'Customer satisfaction guaranteed',
      ],
    },
    {
      id: 'laundry',
      name: 'Goodluck Laundry Services',
      tagline: 'Premium Laundry & Dry Cleaning',
      location: 'Muscat, Oman',
      phone: '+968 9490 0565',
      icon: ShirtIcon,
      image: 'https://images.pexels.com/photos/5591581/pexels-photo-5591581.jpeg',
      description: 'Goodluck Laundry Services offers premium laundry and dry-cleaning services in Oman. We handle your garments with care, ensuring they return fresh, clean, and perfectly pressed.',
      services: [
        'Laundry Services',
        'Dry Cleaning',
        'Ironing & Pressing',
        'Pickup & Delivery',
        'Commercial Laundry Solutions',
        'Specialized Fabric Care',
      ],
      features: [
        'State-of-the-art equipment',
        'Expert fabric handling',
        'Free pickup & delivery',
        'Quick turnaround times',
        'Affordable pricing',
        'Quality guaranteed',
      ],
    },
  ];

  return (
    <div data-testid="companies-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-blue-400 mb-4">
            Our Companies
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-6">
            Three Businesses, <span className="text-blue-400">One Vision</span>
          </h1>
          <p className="text-slate-300 max-w-2xl mx-auto text-lg">
            From advanced packaging manufacturing in India to essential services in Oman, discover our diverse portfolio of companies.
          </p>
        </div>
      </section>

      {/* Companies List */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-24">
          {companies.map((company, index) => (
            <div
              key={company.id}
              id={company.id}
              data-testid={`company-${company.id}`}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                index % 2 !== 0 ? 'lg:grid-flow-dense' : ''
              }`}
            >
              {/* Image */}
              <div className={`relative ${index % 2 !== 0 ? 'lg:col-start-2' : ''}`}>
                <div className={`absolute -z-10 w-72 h-72 bg-blue-100 rounded-2xl ${
                  index % 2 === 0 ? '-top-4 -left-4' : '-top-4 -right-4'
                }`} />
                <img
                  src={company.image}
                  alt={company.name}
                  className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-700 rounded-lg flex items-center justify-center">
                      <company.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-900">{company.name}</p>
                      <p className="text-sm text-slate-600">{company.tagline}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className={index % 2 !== 0 ? 'lg:col-start-1' : ''}>
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                  <MapPin className="w-4 h-4" />
                  <span>{company.location}</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4">
                  {company.name}
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  {company.description}
                </p>

                {/* Products/Services */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">
                    {company.products ? 'Products' : 'Services'}
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {(company.products || company.services).map((item, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <span className="text-sm text-slate-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Features */}
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Why Choose Us</h3>
                  <div className="flex flex-wrap gap-2">
                    {company.features.map((feature, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-slate-100 text-slate-700 text-sm rounded-full"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Contact */}
                <div className="flex flex-wrap items-center gap-4">
                  <a
                    href={`tel:${company.phone.replace(/\s/g, '')}`}
                    className="flex items-center gap-2 text-blue-700 hover:text-blue-800"
                  >
                    <Phone className="w-4 h-4" />
                    <span className="text-sm font-medium">{company.phone}</span>
                  </a>
                  {company.phone2 && (
                    <a
                      href={`tel:${company.phone2.replace(/\s/g, '')}`}
                      className="flex items-center gap-2 text-blue-700 hover:text-blue-800"
                    >
                      <Phone className="w-4 h-4" />
                      <span className="text-sm font-medium">{company.phone2}</span>
                    </a>
                  )}
                </div>

                <Link to={`/contact?service=${company.id}`} className="inline-block mt-6">
                  <Button
                    data-testid={`btn-quote-${company.id}`}
                    className="bg-blue-700 hover:bg-blue-800 text-white rounded-full group"
                  >
                    Request a Quote
                    <ArrowRight className="ml-2 w-4 h-4 btn-arrow" />
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Card className="border-0 bg-blue-700 text-white overflow-hidden">
            <CardContent className="p-12 text-center relative">
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600 rounded-full -translate-y-1/2 translate-x-1/2 opacity-50" />
              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">
                  Ready to Get Started?
                </h2>
                <p className="text-blue-100 mb-8 max-w-xl mx-auto">
                  Contact us today for a free consultation and quote for any of our services.
                </p>
                <Link to="/contact?quote=true">
                  <Button
                    data-testid="companies-cta-btn"
                    className="bg-white text-blue-700 hover:bg-slate-100 rounded-full px-8 py-6 text-base group"
                  >
                    Get a Free Quote
                    <ArrowRight className="ml-2 w-4 h-4 btn-arrow" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Companies;
